import React, { useEffect, useState } from 'react';
import { ReactComponent as LikeIcon } from '../../../assets/Icons/like.svg';
import { ReactComponent as ReplyIcon } from '../../../assets/Icons/reply.svg';
import { ReactComponent as FlagIcon } from '../../../assets/Icons/flag.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/Icons/delete.svg';
import Translate from '../../../Components/Multilanguage/Translate';
import { increment, onValue, ref, set, update } from 'firebase/database';
import initializeFirebase from '../../../firebaseConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getCurrentUTCTimestamp, timeDifferenceWithCurrenttime } from '../../../utils/utils';

if (!window.__liveChatUserCache) window.__liveChatUserCache = {};
const userCache = window.__liveChatUserCache;

const LiveChatCommentReplies = ({ reply, parentComment, projectInfo, isEvent = false, openParentReplyField, onMediaLoad }) => {
  const firebaseDB = initializeFirebase(projectInfo);
  const user = useSelector((state) => state?.user?.value);

  const [liked, setLiked] = useState(false);
  const [repliedUser, setRepliedUser] = useState(() => userCache[reply?.user_id] || {});
  const [repliedTime, setRepliedTime] = useState('');

  const basePath = `comments/${isEvent ? 'event-' : ''}${parentComment?.videoId}/${parentComment?.id}/replies/${reply?.replyId}`;

  useEffect(() => {
    if (!firebaseDB || !reply) return;

    const usersDbRef = ref(firebaseDB, `users/${reply?.user_id}`);
    if (userCache[reply?.user_id]) {
      setRepliedUser(userCache[reply?.user_id]);
    } else {
      onValue(usersDbRef, (snapshot) => {
        const val = snapshot.val() || {};
        userCache[reply?.user_id] = val;
        setRepliedUser(val);
      }, { onlyOnce: true });
    }

    setLiked(!!(reply?.liked_users && reply?.liked_users[user]));

    setRepliedTime(timeDifferenceWithCurrenttime(reply?.created_at));
    const interval = setInterval(() => {
      setRepliedTime(timeDifferenceWithCurrenttime(reply?.created_at));
    }, 20 * 1000);
    return () => clearInterval(interval);
  }, [reply, user]);

  const linkify = (text) => {
    if (!text) return null;
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    return text.split(urlPattern).filter(Boolean).map((part, index) =>
      urlPattern.test(part)
        ? <a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ color: 'blue' }}>{part}</a>
        : part
    );
  };

  const likeHandler = () => {
    if (!firebaseDB) return;
    const dbRef = ref(firebaseDB, basePath);
    const dbRefLikedUsers = ref(firebaseDB, `${basePath}/liked_users/${user}`);
    update(dbRef, { likes: increment(liked ? -1 : 1) });
    set(dbRefLikedUsers, liked ? 0 : 1);
    setLiked((prev) => !prev);
  };

  const deleteHandler = () => {
    if (!firebaseDB) return;
    const dbRef = ref(firebaseDB, basePath);
    const parentRef = ref(firebaseDB, `comments/${isEvent ? 'event-' : ''}${parentComment?.videoId}/${parentComment?.id}`);
    update(parentRef, { reply_count: increment(-1) });
    update(dbRef, { delete_status: true, deleted_at: getCurrentUTCTimestamp() });
    toast.success(<Translate textKey={'reply_deleted_successfully'} />, { position: 'top-center' });
  };

  const userHasReported = () => !!(reply?.reported_users && reply?.reported_users[user]);

  const reportHandler = () => {
    if (!firebaseDB) return;
    if (userHasReported()) {
      toast.error(<Translate textKey={'already_reported'} />, { position: 'top-center' });
      return;
    }
    const dbRef = ref(firebaseDB, basePath);
    const dbRefReported = ref(firebaseDB, `${basePath}/reported_users/${user}`);
    update(dbRef, { reported_count: increment(1) });
    set(dbRefReported, 1);
    toast.success(<Translate textKey={'successfully_reported'} />, { position: 'top-center' });
  };

  return (
    <div className="replyMessagesContainer">
      <div className="containers">
        <div className="left">
          {repliedUser?.image ? (
            <div className="profileImageContainer">
              <img src={repliedUser.image} alt="Profile" />
            </div>
          ) : (
            <span className="nameAvatar">{repliedUser?.name?.slice(0, 1)?.toUpperCase()}</span>
          )}
        </div>
        <div className="right">
          <div className="nameTimeRow">
            <h1 className="name">{repliedUser?.name}</h1>
            <span className="time">{repliedTime}</span>
          </div>
          {reply?.comment_text && <p className="comment">{linkify(reply.comment_text)}</p>}
          {reply?.image?.url && (
            <div className="comment-image-container">
              <img src={reply.image.url} alt="Reply" onLoad={() => { if (onMediaLoad) onMediaLoad(); }} />
            </div>
          )}
          <div className="actions">
            <span className="item" onClick={likeHandler}>
              <span className="icon">
                <LikeIcon style={liked ? { fill: 'red' } : {}} />
              </span>
              {reply?.likes > 0 && reply.likes}{' '}
              {reply?.likes >= 2 ? <Translate textKey={'likes'} /> : <Translate textKey={'like'} />}
            </span>
            {reply?.user_id === Number(user) ? (
              <span className="item" onClick={deleteHandler}>
                <span className="icon"><DeleteIcon /></span>
                <Translate textKey={'delete'} />
              </span>
            ) : (
              <span className="item" onClick={reportHandler}>
                <span className="icon"><FlagIcon /></span>
                <Translate textKey={'report'} />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveChatCommentReplies;
